import { useEffect, useState } from 'react';
import { IoMailOutline } from 'react-icons/io5';
import { GoArrowLeft } from 'react-icons/go';
import { useLocation } from 'react-router-dom';
import { IoIosClose } from 'react-icons/io';
import { LuPlus } from 'react-icons/lu';
import deleteIcon from '../../../assets/images/delete-icon.svg';
import timeLimitIcon from '../../../assets/images/time-limit-icon.svg';
import EmailEditorTinyMCE from '../TextEditor/TextEditor';
import campaignService from '../../../services/campaignService';
import Toast from '../../molecules/Toast';
import ModalContainer from '../../molecules/ModalContainer';
import DeleteStep from './DeleteStep';
import Button from '../../molecules/Button';
import { StyledCampaignSequence } from './CreateCompaign.styles';

const Step6 = ({
  setData,
  data,
  NextStep,
  submit,
  goBack,
  step,
  setStep,
  totalStep = 7,
  type,
  defaultLocked = false,
  isEdit,
}) => {
  const location = useLocation();
  const campaignStatus = location?.state?.status;
  const campaignType = location?.state?.campaign_type || data?.campaignType;
  const defaultSteps = campaignType === 'ai' || data?.step5?.isEnrichment;
  const [loading, setLoading] = useState(false);
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [activeVariantIndex, setActiveVariantIndex] = useState(0);
  const [unsubscribeOption, setUnsubscribeOption] = useState(true);
  const [sequencesData, setSequencesData] = useState();
  const [addStep, setAddStep] = useState(() => {
    const createStep = (delayDays, seqNumber, frequency = 'Days') => ({
      delayDays,
      seqNumber,
      frequency,
      variants: [{ label: 'A', subject: defaultSteps ? '{{subject_one}}' : 'Hi {{first_name}}', body: '' }],
    });

    if (defaultSteps) {
      return [createStep(0, 1), createStep(1, 2), createStep(1, 3)];
    }

    return [createStep(0, 1)];
  });
  const getDefaultVariantBody = (stepIndex, signature = '') => {
    const messages = ['{{message_one}}', '{{message_two}}', '{{message_three}}'];
    const message = messages[stepIndex] || '{{message_one}}';
    const greetingPart =
      campaignType !== 'ai'
        ? `<span>{{greeting}},</span><br /><br />`
        : '';
    return `
  <div>
 ${greetingPart}
  ${message}
  <br />
  <br />
    <span>${signature}</span>
  </div>
`;
  };
  async function handleSubmit() {
    try {
      const invalidDelay = addStep.some((step, index) => index !== 0 && step.delayDays < 1);

      if (invalidDelay) {
        Toast({
          type: 'error',
          message: 'Delay must be greater than 0',
        });
        return;
      }

      // Check if any variant has empty subject or body
      for (let stepIndex = 0; stepIndex < addStep.length; stepIndex++) {
        const step = addStep[stepIndex];
        for (let variantIndex = 0; variantIndex < step.variants.length; variantIndex++) {
          const variant = step.variants[variantIndex];
          const hasEmptySubject = !variant.subject || !variant.subject.trim();
          const bodyContent = extractBodyFromHTML(variant.body);
          const hasEmptyBody = !variant.body || !variant.body.trim() || !bodyContent.trim();

          if (hasEmptySubject || hasEmptyBody) {
            const missingFields = [];
            if (hasEmptySubject) {
              missingFields.push('subject');
            }
            if (hasEmptyBody) {
              missingFields.push('body');
            }

            const stepTitle = stepIndex === 0 ? 'Initial Email' : `Step ${stepIndex + 1}`;
            Toast({
              type: 'error',
              message: `Please fill in the ${missingFields.join(' and ')} for variant ${variant.label} in ${stepTitle}`,
            });
            return;
          }
        }
      }

      setLoading(true);
      const payload = {
        campaignId:
          data?.step2?.campaignId || data?.step6?.campaignId || data?.campaignId || location?.state?.campaign_id,
        sequences: addStep?.map((item, index) => ({
          seqNumber: item?.seqNumber,
          seqType: 'EMAIL',
          seqDelayDetails: {
            delayInDays: item?.frequency === 'Weeks' ? item?.delayDays * 7 : item?.delayDays,
            frequency: item?.frequency || 'Days',
          },
          seqVariants: item.variants.map(v => ({
            subject: v.subject,
            emailBody: `<!DOCTYPE html><html lang="en"><head><meta charset="UTF-8" /><meta name="viewport" content="width=device-width, initial-scale=1" /><style>p, br { margin: 0; padding: 0; }</style></head><body>${v.body}</body></html>`,
            variantLabel: v.label,
          })),
          variantDistributionType: 'MANUAL_EQUAL',
        })),
        unsubscribe_url: unsubscribeOption,
      };
      const res = await campaignService.addSequenceToCampaign(payload);
      if (res.success) {
        Toast({ type: 'success', message: res.message });
        setData(prev => ({ ...prev, step6: payload }));
        NextStep();
      }
    } catch (err) {
      Toast({ type: 'error', message: err.message });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (campaignType === 'ai' || data?.step5?.isEnrichment) {
      const getSubjectByStep = index => {
        const subjects = ['{{subject_one}}', '{{subject_two}}', '{{subject_three}}'];
        return subjects[index] || '{{subject_one}}';
      };
      setAddStep(prev => {
        return prev.map((step, stepIndex) => ({
          ...step,
          variants: step.variants.map(variant => ({
            ...variant,
            subject: getSubjectByStep(stepIndex),
            body: getDefaultVariantBody(stepIndex, campaignType === 'ai' ? "" : data?.step5?.signature || ''),
          })),
        }));
      });
    }
  }, [campaignType, data?.step5?.isEnrichment, data?.step11?.signature]);

  function extractBodyFromHTML(html = '') {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc?.body?.innerHTML || '';
  }

  function handleAddVariant(stepIndex) {
    const currentVariants = addStep[stepIndex].variants;
    const nextLabel = String.fromCharCode(65 + currentVariants.length);
    const newVariant = { label: nextLabel, subject: 'Hi {{first_name}}', body: '' };
    if (!newVariant.subject?.trim()) {
      Toast({ type: 'error', message: 'Please enter the subject' });
      return;
    }

    setAddStep(prev =>
      prev.map((step, index) => (index === stepIndex ? { ...step, variants: [...step.variants, newVariant] } : step)),
    );

    setActiveStepIndex(stepIndex);
    setActiveVariantIndex(currentVariants.length);
  }

  function handleRemoveVariant(stepIndex, variantIndex) {
    const currentVariants = addStep[stepIndex].variants;
    if (currentVariants.length === 1) return;

    const updatedVariants = currentVariants.filter((_, index) => index !== variantIndex);
    const relabeledVariants = updatedVariants.map((v, i) => ({
      ...v,
      label: String.fromCharCode(65 + i),
    }));

    setAddStep(prev =>
      prev.map((step, index) => (index === stepIndex ? { ...step, variants: relabeledVariants } : step)),
    );

    // Adjust active indices
    if (activeStepIndex === stepIndex) {
      if (activeVariantIndex === variantIndex) {
        setActiveVariantIndex(variantIndex === 0 ? 0 : variantIndex - 1);
      } else if (activeVariantIndex > variantIndex) {
        setActiveVariantIndex(prev => prev - 1);
      }
    }
  }

  function handleAddStep() {
    const newStep = {
      seqNumber: addStep.length + 1,
      delayDays: 1,
      frequency: 'Days',
      variants: [{ label: 'A', subject: 'Hi {{first_name}}', body: '' }],
    };
    setAddStep(prev => [...prev, newStep]);
    setActiveStepIndex(addStep.length);
    setActiveVariantIndex(0);
  }

  async function handleDeleteStep(stepIndex, onClose) {
    // Check if this is a saved sequence (exists in sequencesData) or a newly added step
    const isSavedSequence = isEdit && sequencesData?.length > 0 && sequencesData[stepIndex]?.id;

    if (isSavedSequence) {
      // This is a saved sequence, call API to delete it
      const sequenceToDelete = sequencesData[stepIndex];

      const payload = {
        campaignId: sequenceToDelete?.campaignId,
        sequenceId: sequenceToDelete?.id,
        seqNumber: sequenceToDelete?.seqNumber,
      };

      try {
        setLoading(true);
        const res = await campaignService.deleteCampaignSequence(payload);
        if (res?.success) {
          Toast({ type: 'success', message: res?.message });

          // Remove the deleted sequence from addStep
          const updatedSteps = addStep
            ?.filter((_, index) => index !== stepIndex)
            .map((step, idx) => ({ ...step, seqNumber: idx + 1 }));

          setAddStep(updatedSteps);

          // Update sequencesData to remove the deleted sequence
          const updatedSequencesData = sequencesData.filter((_, index) => index !== stepIndex);
          setSequencesData(updatedSequencesData);

          // Adjust active indices
          if (activeStepIndex >= updatedSteps.length) {
            setActiveStepIndex(Math.max(0, updatedSteps.length - 1));
            setActiveVariantIndex(0);
          }

          onClose();
        }
      } catch (error) {
        Toast({ type: 'error', message: error?.message });
      } finally {
        setLoading(false);
      }
    } else {
      // This is a newly added step (not saved yet) or non-edit mode, just remove from local state
      const updatedSteps = addStep
        ?.filter((_, index) => index !== stepIndex)
        .map((step, idx) => ({ ...step, seqNumber: idx + 1 }));

      setAddStep(updatedSteps);

      if (activeStepIndex >= updatedSteps.length) {
        setActiveStepIndex(Math.max(0, updatedSteps.length - 1));
        setActiveVariantIndex(0);
      }

      onClose();
    }
  }

  function handleVariantClick(stepIndex, variantIndex) {
    setActiveStepIndex(stepIndex);
    setActiveVariantIndex(variantIndex);
  }

  function updateVariantSubject(value) {
    setAddStep(prev =>
      prev.map((step, stepIndex) =>
        stepIndex === activeStepIndex
          ? {
            ...step,
            variants: step.variants.map((variant, variantIndex) =>
              variantIndex === activeVariantIndex ? { ...variant, subject: value } : variant,
            ),
          }
          : step,
      ),
    );
  }

  function updateVariantBody(value) {
    setAddStep(prev =>
      prev.map((step, stepIndex) =>
        stepIndex === activeStepIndex
          ? {
            ...step,
            variants: step.variants.map((variant, variantIndex) =>
              variantIndex === activeVariantIndex ? { ...variant, body: value } : variant,
            ),
          }
          : step,
      ),
    );
  }

  function handleIsNotDraft() {
    if (!data?.isEnrichment) {
      setStep(3);
    } else if (data?.leads_type === 'houdiny_leads') {
      setStep(6);
    } else {
      goBack();
    }
  }

  function handelBack() {
    if (campaignType === 'ai') {
      setStep(11);
    } else {
      setStep(2);
    }
  }

  const currentActiveVariant = addStep[activeStepIndex]?.variants[activeVariantIndex];
  // Fetch sequences when campaign_id is available (for edit mode)
  useEffect(() => {
    if (isEdit) {
      const getSequences = async () => {
        try {
          setLoading(true);
          const res = await campaignService.getCampaignSequences(location?.state?.campaign_id);
          setSequencesData(res.data);

          // Update unsubscribeOption if unsubscribe_url exists in response
          if (res.data && typeof res.data.unsubscribe_url !== 'undefined') {
            // Normalize value to boolean (handle string 'true'/'false' from API)
            const normalizedValue = res.data.unsubscribe_url === 'true' || res.data.unsubscribe_url === true;
            setUnsubscribeOption(normalizedValue);
          } else if (res.unsubscribe_url !== undefined) {
            // Check if it's at the top level of response
            const normalizedValue = res.unsubscribe_url === 'true' || res.unsubscribe_url === true;
            setUnsubscribeOption(normalizedValue);
          }
        } catch (err) {
          Toast({ type: 'error', message: err.message });
        } finally {
          setLoading(false);
        }
      };
      getSequences();
    }
  }, [location?.state?.campaign_id, isEdit]);

  // Parse sequencesData when it's loaded and update addStep
  useEffect(() => {
    if (sequencesData?.length > 0) {
      const parsedSteps = sequencesData.map(seq => ({
        seqNumber: seq?.seqNumber,
        delayDays: seq?.delayInDays || 0,
        frequency: seq?.frequency || 'Days',
        variants:
          seq?.variants?.map(variant => ({
            label: variant.variantLabel,
            subject: variant.subject,
            body: extractBodyFromHTML(variant?.emailBody),
          })) || [],
      }));

      setAddStep(parsedSteps);
    }
  }, [sequencesData]);

  const getStepTitle = stepIndex => {
    if (stepIndex === 0) return 'Initial Email';
    const ordinals = ['Second', 'Third', 'Fourth', 'Fifth', 'Sixth', 'Seventh', 'Eighth', 'Ninth', 'Tenth'];
    return stepIndex < ordinals.length + 1 ? `${ordinals[stepIndex - 1]} Email` : `${stepIndex + 1}th Email`;
  };



  return (
    <StyledCampaignSequence $isAiCampaign={campaignType === 'ai'}>
      <div className="head">
        {data?.isEnrichment === false && (!data?.step6?.sequences || data?.step6?.sequences?.length === 0) && (
          <button onClick={handleIsNotDraft}>
            <GoArrowLeft size={25} />
          </button>
        )}
      </div>
      <div className="content-holder">
        <div className="steps-bar">
          <div className="steps-bar-content">
            {addStep?.map((item, stepIndex) => (
              <div key={stepIndex}>
                {stepIndex !== 0 && (
                  <div className="step-holder waiting-step">
                    <div className="step day-limit">
                      <span className="text">Wait for </span>
                      <input
                        type="number"
                        value={item?.delayDays}
                        onChange={e => {
                          const updated = [...addStep];
                          updated[stepIndex].delayDays = Number(e.target.value);
                          setAddStep(updated);
                        }}
                        disabled={campaignStatus === 'completed'}
                      />
                      <div className="choose-box">
                        <div className="btn-wrap">
                          <button
                            className={item?.frequency === 'Days' ? 'active' : ''}
                            onClick={() => {
                              if (campaignStatus === 'completed') return;
                              const updated = [...addStep];
                              updated[stepIndex].frequency = 'Days';
                              setAddStep(updated);
                            }}
                            disabled={campaignStatus === 'completed'}>
                            {item?.delayDays > 1 ? 'days' : 'day'}
                          </button>
                          <button
                            className={item?.frequency === 'Weeks' ? 'active' : ''}
                            onClick={() => {
                              if (campaignStatus === 'completed') return;
                              const updated = [...addStep];
                              updated[stepIndex].frequency = 'Weeks';
                              setAddStep(updated);
                            }}
                            disabled={campaignStatus === 'completed'}>
                            {item?.delayDays > 1 ? 'weeks' : 'week'}
                          </button>
                        </div>
                        <span className="text">then</span>
                      </div>
                      {addStep?.length !== 1 && campaignStatus !== 'completed' && campaignType !== 'ai' && (
                        <ModalContainer
                          width={678}
                          btnComponent={({ onClick }) => (
                            <button className="btn-delete" onClick={onClick}>
                              <img src={deleteIcon} alt="deleteIcon" />
                            </button>
                          )}
                          content={({ onClose }) => (
                            <DeleteStep
                              onClose={onClose}
                              handleDeleteStep={formData => handleDeleteStep(stepIndex, onClose)}
                            />
                          )}
                        />
                      )}
                    </div>
                  </div>
                )}
                <div className="step-holder">
                  <div className="step">
                    <div className="variant-head">
                      <div className="wrap">
                        <div className="icon-holder">
                          <IoMailOutline size={24} />
                        </div>
                        <div className="box">
                          <span className="step-title">{getStepTitle(stepIndex)}</span>
                          {/* <span className="step-subtitle">Immediate</span> */}
                        </div>
                      </div>
                    </div>
                    {campaignType !== 'ai' && (

                      <div className="step-name-wrap">
                        {item?.variants?.map((variant, variantIndex) => (
                          <div
                            className={
                              activeStepIndex === stepIndex && activeVariantIndex === variantIndex
                                ? 'step-name-holder active'
                                : 'step-name-holder'
                            }
                            key={variantIndex}>
                            <div className="name-holder" onClick={() => handleVariantClick(stepIndex, variantIndex)}>
                              <span className={`step-label label-${(variant.label || 'a').toLowerCase()}`}>
                                {variant?.label}
                              </span>
                              <span className="step-name">subject: {variant?.subject}</span>
                            </div>
                            {item.variants?.length !== 1 && campaignStatus !== 'completed' && (
                              <button className="remove-icon" onClick={() => handleRemoveVariant(stepIndex, variantIndex)}>
                                <IoIosClose size={20} />
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {campaignStatus !== 'completed' && campaignType !== "ai" && (
                    <div className="varaint-btn-holder">
                      <button className="add-variant" onClick={() => handleAddVariant(stepIndex)}>
                        + Variant
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {campaignStatus !== 'completed' && campaignType !== "ai" && (
            <div className="add-step-holder">
              <button type="button" onClick={handleAddStep}>
                <LuPlus className="icon" /> Step
              </button>
            </div>
          )}
        </div>
        {campaignType !== 'ai' &&
          <EmailEditorTinyMCE
            subject={currentActiveVariant?.subject || ''}
            setSubject={updateVariantSubject}
            body={currentActiveVariant?.body || ''}
            setBody={updateVariantBody}
            handleSubmit={handleSubmit}
            setUnsubscribeOption={setUnsubscribeOption}
            unsubscribeOption={unsubscribeOption}
            loading={loading}
            variantLabel={currentActiveVariant?.label || ''}
            data={data}
            defaultLocked={campaignType === 'ai' || campaignStatus === 'completed'}
            activeStepIndex={activeStepIndex}
          />
        }
      </div>
      <div className="btn-holder">
        {!data?.step1?.list_id && !isEdit && (
          <Button
            className="editMode"
            newStyle
            rounded
            variant="outline"
            width="155px"
            type="button"
            onClick={handelBack}>
            Back
          </Button>
        )}
        {campaignStatus !== 'completed' && (
          <Button
            newStyle
            variant="darkBlue"
            type="submit"
            rounded
            width="155px"
            loader={loading}
            disabled={loading}
            onClick={() => handleSubmit()}>
            {isEdit ? 'Save' : 'Next'}
          </Button>
        )}
      </div>
    </StyledCampaignSequence>
  );
};

export default Step6;
